/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import PropTypes from 'prop-types';
import React, {
  useEffect, useMemo, useCallback, useReducer,
} from 'react';
import { useMutation } from '@apollo/client';

import { Map, List } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import Alert          from '@mui/material/Alert';
import Button         from '@mui/material/Button';
import LoadingButton  from '@mui/lab/LoadingButton';
import Grid           from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Typography     from '@mui/material/Typography';

import ChevronLeft  from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { Display } from 'components';
import { isUserAgentAllowedForRecording } from 'helpers/hardware_access';
import { clearItem } from 'helpers/local_storage';
import useFormStateHandler from 'hooks/LandingPage/Answers/useFormStateHandler';
import ApolloWrapper from 'pages/LandingPage/ApolloWrapper';
import { CANDIDATE_APPLICATION_PROMPT_RESPONSE_INPUT } from 'queries/prompt/create';
import CandidateApplication from 'records/candidate_application';

import PromptResponse from './PromptResponse';
import NotSupportedModal from './NotSupportedModal';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const RECORD_TYPES = ['audio', 'video'];
// API Max per request is 100 but 10 to make it faster.
// (Long Form only, will autosave when 10 answers are in queue)
const MAX_RESPONSES_PER_REQUEST = 10;

/* ––
 * ––– State reducer
 * –––––––––––––––––––––––––––––––– */
const StateAction = {
  QueuePersistenceBatches:        'QueuePersistenceBatches',
  ClearFirstPersistenceBatch:     'ClearFirstPersistenceBatch',
  ResetPersistenceData:           'ResetPersistenceData',
  UpdatePersistenceErrorStatus:   'UpdatePersistenceErrorStatus',
  UpdateUnsupportedBrowserStatus: 'UpdateUnsupportedBrowserStatus',
  SetCurrentQuestionIndex:        'SetCurrentQuestionIndex',
  IncreaseCurrentQuestionIndex:   'IncreaseCurrentQuestionIndex',
  DecrementCurrentQuestionIndex:  'DecrementCurrentQuestionIndex',
};

const initialState = new Map({
  unsupportedBrowser:   false,
  persistenceQueue:     new List(),
  persistenceMetadata:  new Map(),
  persistenceCompleted: false,
  showPersistenceError: false,
  currentQuestionIndex: 0,
});

function stateReducer(state, action) {
  switch (action.type) {
    case StateAction.QueuePersistenceBatches: {
      const shouldCompleteImmediately = state.get('persistenceQueue').size === 0 && action.batches.size === 0;

      // Last metadata value will take precedence
      return state
        .update('persistenceQueue', (queue) => queue.concat(action.batches))
        .update('persistenceMetadata', (metadata) => metadata.merge(action.metadata))
        .set('persistenceCompleted', shouldCompleteImmediately);
    }
    case StateAction.ClearFirstPersistenceBatch: {
      const udpatedState = state.update('persistenceQueue', (queue) => queue.shift());

      return udpatedState.set('persistenceCompleted', udpatedState.get('persistenceQueue').size === 0);
    }
    case StateAction.ResetPersistenceData: {
      return state
        .update('persistenceQueue', (queue) => (queue.size > 0 ? queue.clear() : queue))
        .set('persistenceMetadata', new Map())
        .set('persistenceCompleted', false);
    }
    case StateAction.UpdatePersistenceErrorStatus: {
      return state.set('showPersistenceError', action.value);
    }
    case StateAction.SetCurrentQuestionIndex: {
      return state.set('currentQuestionIndex', action.value);
    }
    case StateAction.IncreaseCurrentQuestionIndex: {
      return state.update('currentQuestionIndex', (index) => index + 1);
    }
    case StateAction.DecrementCurrentQuestionIndex: {
      return state.update('currentQuestionIndex', (index) => index - 1);
    }
    case StateAction.UpdateUnsupportedBrowserStatus: {
      return state.set('unsupportedBrowser', action.value);
    }
    default: {
      throw new Error(`Invalid action type: ${action.type}`);
    }
  }
}

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function Answers({
  application,
  beDone,
  isLongForm,
  isAlwaysSubmittable,
  checkFollowups,
  refetched,
  allowLandingPageResponseResubmit,
  country,
  maxUnsavedBeforeAutosave = MAX_RESPONSES_PER_REQUEST,
}) {
  /* –– State declaration
   * –––––––––––––––––––––––––––––––– */
  // General state values
  const [state, dispatch] = useReducer(stateReducer, initialState);

  const unsupportedBrowser = state.get('unsupportedBrowser');
  const persistenceQueue = state.get('persistenceQueue');
  const persistenceMetadata = state.get('persistenceMetadata');
  const persistenceCompleted = state.get('persistenceCompleted');
  const showPersistenceError = state.get('showPersistenceError');
  const currentQuestionIndex = state.get('currentQuestionIndex');

  /* –– Computed values
   * –––––––––––––––––––––––––––––––– */
  const questions = useMemo(
    () => application.responses
      .filter((response) => isEmpty(response.get('value') || response.get('docs') || response.get('url')))
      .filter((response) => response.getIn(['prompt', 'typeName']) === 'Question'),
    [application.responses],
  );

  const singleQuestionRequired = useMemo(
    () => questions.some((question) => {
      const timerEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'timer_enabled']);
      const timer = question.getIn(['prompt', 'metadata', 'parameters', 'timer']);
      const disableReschedule = question.getIn(['prompt', 'metadata', 'parameters', 'disable_reschedule']);

      return (timerEnabled && timer) || disableReschedule;
    }),
    [questions],
  );

  const singleQuestionMode = useMemo(
    () => singleQuestionRequired || !isLongForm,
    [singleQuestionRequired, isLongForm],
  );

  const showBackButton = useMemo(
    () => (singleQuestionRequired ? false : allowLandingPageResponseResubmit),
    [singleQuestionRequired, allowLandingPageResponseResubmit],
  );

  const [submitResponses, responsesSubmissionResult] = useMutation(
    CANDIDATE_APPLICATION_PROMPT_RESPONSE_INPUT, {
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('Error submitting responses: ', error);
      },
    },
  );

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const incrementQuestionIndex = useCallback(
    () => dispatch({ type: StateAction.IncreaseCurrentQuestionIndex }),
    [],
  );

  const decrementQuestionIndex = useCallback(
    () => dispatch({ type: StateAction.DecrementCurrentQuestionIndex }),
    [],
  );

  const persistResponseBatches = useCallback((responseBatches, metadata) => {
    dispatch({ type: StateAction.QueuePersistenceBatches, batches: responseBatches, metadata });
  }, []);

  const generateResponseSaveMetadata = useCallback((response) => {
    const metadata = {};

    if (response.get('forced')) {
      metadata.navigateToNextQuestion = true;

      if (currentQuestionIndex + 1 >= questions.size) {
        metadata.submitted = true;
      }
    }

    return metadata;
  }, [currentQuestionIndex, questions]);

  const {
    responses, validResponses,
    submittable, processing,
    responseProcessingError,
    processResponse,
    saveFormProgress,
    retryFailedResponseProcessing,
    submitForm,
    reportBusyQuestion,
    reportIdleQuestion,
  } = useFormStateHandler(
    application.id,
    questions,
    persistResponseBatches,
    generateResponseSaveMetadata,
    { flushThreshold: maxUnsavedBeforeAutosave },
  );

  const submitApplication = useCallback(() => submitForm({ submitted: true }), [submitForm]);

  /* –– Effects
   * –––––––––––––––––––––––––––––––– */
  useEffect(() => {
    if (questions.size === 0) {
      beDone();
    }
  }, [questions]);

  useEffect(() => {
    if (!isUserAgentAllowedForRecording(navigator.userAgent)) {
      const hasRecordingQuestion = questions.some((question) => RECORD_TYPES.includes(question.getIn(['prompt', 'categoryName'])));

      dispatch({ type: StateAction.UpdateUnsupportedBrowserStatus, value: hasRecordingQuestion });
    }
  }, [questions]);

  useEffect(() => {
    if (refetched) {
      dispatch({ type: StateAction.SetCurrentQuestionIndex, value: 0 });
    }
  }, [refetched]);

  useEffect(() => {
    if (persistenceQueue.size !== 0) {
      const responseBatch = persistenceQueue.first();

      submitResponses({ variables: { response: responseBatch.toJS() } });

      responseBatch.forEach(
        (response) => clearItem(`${application.id}-${response.get('promptId')}`),
      );
    }
  }, [persistenceQueue]);

  useEffect(() => {
    if (responsesSubmissionResult.data !== undefined) {
      dispatch({ type: StateAction.ClearFirstPersistenceBatch });
    } else if (responsesSubmissionResult.error !== undefined) {
      dispatch({ type: StateAction.ResetPersistenceData });

      if (persistenceMetadata.get('submitted')) {
        dispatch({ type: StateAction.UpdatePersistenceErrorStatus, value: true });
      }
    }
  }, [responsesSubmissionResult.data, responsesSubmissionResult.error]);

  useEffect(() => {
    if (persistenceCompleted) {
      if (
        persistenceMetadata.get('submitted')
        || (persistenceMetadata.get('navigateToNextQuestion') && currentQuestionIndex + 1 >= questions.size)
      ) {
        checkFollowups();
      } else if (persistenceMetadata.get('navigateToNextQuestion')) {
        incrementQuestionIndex();
      }

      dispatch({ type: StateAction.ResetPersistenceData });
    }
  }, [persistenceCompleted]);

  useEffect(() => {
    if (singleQuestionRequired || (!isLongForm && !isAlwaysSubmittable)) {
      saveFormProgress();
    }
  }, [currentQuestionIndex]);

  /* –– Rendering values
   * –––––––––––––––––––––––––––––––– */
  const completionProgress = useMemo(
    () => Math.round((validResponses.size / questions.size) * 100),
    [validResponses, questions],
  );

  const isBusy = processing || persistenceQueue.size > 0;
  const shouldBlockAnyInteraction = processing || (persistenceQueue.size > 0 && persistenceMetadata.get('submitted'));
  const shouldDisableBackNavigation = currentQuestionIndex === 0 || isBusy;
  const shouldDisableForwardNavigation = isBusy
    || (!isAlwaysSubmittable && currentQuestionIndex >= validResponses.size)
    || currentQuestionIndex === (questions.size - 1);

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  return (
    <ApolloWrapper token={application.bearerToken}>
      {
        questions.map((question, index) => {
          const currentResponse = responses
            .find((response) => response.get('promptId') === question.getIn(['prompt', 'id']));

          if (singleQuestionMode && currentQuestionIndex !== index) return null;

          return (
            <PromptResponse
              key={question.getIn(['prompt', 'id'])}
              application={application}
              question={question}
              response={currentResponse}
              onChange={processResponse}
              onProcessing={reportBusyQuestion}
              onProcessingCompleted={reportIdleQuestion}
              index={index}
              disabled={shouldBlockAnyInteraction}
              country={country}
            />
          );
        })
      }

      <Display if={singleQuestionMode}>
        <Grid container item xs={12} spacing={1} sx={{ mt: 5 }}>
          <Grid item md={10} xs={8}>
            <Typography variant="body2" color="text.secondary">{t('eos.landing_page.text.question_progress', { current: validResponses.size, size: questions.size })}</Typography>
          </Grid>
          <Grid item xs={12}>
            <LinearProgress value={completionProgress} color="primary" variant="determinate" sx={{ height: 10 }} />
          </Grid>
        </Grid>
        <Display if={questions.size > 1}>
          <Grid container item xs={12} spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Display if={showBackButton}>
                <Button disabled={shouldDisableBackNavigation} variant="contained" color="primary" size="small" onClick={decrementQuestionIndex} sx={{ mr: 1 }} data-testid="prev"><ChevronLeft fontSize="small" /></Button>
              </Display>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button disabled={shouldDisableForwardNavigation} variant="contained" color="primary" size="small" onClick={incrementQuestionIndex} data-testid="next"><ChevronRight fontSize="small" /></Button>
            </Grid>
          </Grid>
        </Display>
      </Display>

      <Display if={responseProcessingError}>
        <Grid item xs={12} sx={{ display: 'flex', mt: 4, justifyContent: 'center' }}>
          <Alert severity="error">{responseProcessingError}</Alert>
          <Button variant="contained" color="primary" size="small" onClick={retryFailedResponseProcessing} data-testid="answers-index-retry-button">{t('eos.landing_page.label.retry')}</Button>
        </Grid>
      </Display>

      <Grid item xs={12} sx={{ display: 'flex', mt: 4, justifyContent: 'center' }}>
        <Display id="error" if={responsesSubmissionResult.error && showPersistenceError}>
          <Alert severity="error" sx={{ mr: 3 }}>{t('eos.landing_page.text.application_invalid')}</Alert>
        </Display>
        {/* TODO: display only if last question */}
        <LoadingButton disabled={!(submittable || isAlwaysSubmittable)} loading={isBusy} disableElevation variant="contained" color="primary" size="large" onClick={submitApplication}>{t('eos.landing_page.label.submit')}</LoadingButton>
      </Grid>

      <NotSupportedModal open={unsupportedBrowser} />
    </ApolloWrapper>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
Answers.propTypes = {
  application:                      PropTypes.instanceOf(CandidateApplication).isRequired,
  beDone:                           PropTypes.func.isRequired,
  isLongForm:                       PropTypes.bool.isRequired,
  isAlwaysSubmittable:              PropTypes.bool.isRequired,
  checkFollowups:                   PropTypes.func.isRequired,
  refetched:                        PropTypes.bool.isRequired,
  allowLandingPageResponseResubmit: PropTypes.bool.isRequired,
  country:                          PropTypes.string.isRequired,
  maxUnsavedBeforeAutosave:         PropTypes.number,
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default Answers;
